import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, X } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function DeleteAccountModal({ open, onClose, onDeleted }) {
  const { t } = useI18n();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const canConfirm = confirmText.trim().toUpperCase() === "DELETE";

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await api.delete("/auth/me");
      toast.success(t("account_deleted"));
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10005] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        data-testid="delete-account-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl"
      >
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="mt-1 rounded-full bg-[#FBF3F1] p-2 text-[#7A2A2A]">
              <AlertTriangle size={20} strokeWidth={1.75} />
            </div>
            <div>
              <div className="overline text-[#7A2A2A]">{t("danger_zone")}</div>
              <h2 className="mt-1 font-serif-display text-2xl text-[#143122]">
                {t("delete_account")}
              </h2>
            </div>
          </div>
          <button
            type="button"
            data-testid="delete-modal-close"
            onClick={onClose}
            className="text-[#1A1F1C]/50 hover:text-[#143122]"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <p className="text-sm text-[#1A1F1C]/85 leading-relaxed">
            {t("delete_account_warning")}
          </p>
          <ul className="text-sm text-[#1A1F1C]/75 list-disc pl-5 space-y-1">
            <li>{t("delete_bullet_profile")}</li>
            <li>{t("delete_bullet_listings")}</li>
            <li>{t("delete_bullet_messages")}</li>
            <li>{t("delete_bullet_reviews")}</li>
            <li>{t("delete_bullet_bookings")}</li>
          </ul>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              {t("delete_confirm_label")}
            </label>
            <input
              data-testid="delete-confirm-input"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="kav-input"
              autoFocus
            />
          </div>
        </div>

        <div className="flex gap-3 p-6 pt-2 border-t border-[#E6E2D8]">
          <button
            type="button"
            data-testid="delete-cancel-btn"
            onClick={onClose}
            className="flex-1 rounded-md border border-[#143122]/20 text-[#143122] hover:bg-[#F4F1E9] px-4 py-2 text-sm font-medium"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            data-testid="delete-confirm-btn"
            disabled={!canConfirm || busy}
            onClick={submit}
            className="flex-1 rounded-md bg-[#7A2A2A] text-white hover:bg-[#5f2020] disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium"
          >
            {busy ? "..." : t("delete_account_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
